import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Pointer } from 'lucide-react'

interface PhotoNavGuideProps {
  onDismiss: () => void
}

/**
 * One-time coachmark shown over a candidate's photo the first time the
 * viewer encounters a candidate with more than one photo. Teaches the
 * left/right tap-to-cycle gesture already implemented by the tap-zone
 * buttons underneath; tapping anywhere dismisses it for good.
 */
export default function PhotoNavGuide({ onDismiss }: PhotoNavGuideProps) {
  const { t } = useTranslation()

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 bg-black/40"
      onClick={(e) => {
        e.stopPropagation()
        onDismiss()
      }}
    >
      {/* Dashed divider aligned with the real w-2/5 / w-3/5 tap-zone split */}
      <div className="absolute inset-y-0 left-[40%] border-l border-dashed border-white/60" />

      <div className="absolute inset-y-0 left-0 flex w-2/5 flex-col items-center justify-center gap-2 text-white">
        <Pointer className="h-7 w-7 scale-x-[-1]" />
        <span className="text-center text-xs font-semibold uppercase tracking-wide">
          {t('photoGuide.previousPhotoLabel')}
        </span>
      </div>

      <div className="absolute inset-y-0 right-0 flex w-3/5 flex-col items-center justify-center gap-2 text-white">
        <Pointer className="h-7 w-7" />
        <span className="text-center text-xs font-semibold uppercase tracking-wide">
          {t('photoGuide.nextPhotoLabel')}
        </span>
      </div>
    </motion.div>
  )
}
